import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import { getFaviconUrl } from "@/utils/favicon";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { WebClipFavicon, type WebClipFaviconStatus } from "./WebClipFavicon";
import { recordRecentWebClip } from "./webclipRecents";
import type { IconItem, IconKind } from "./types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (item: IconItem) => void;
}

export function AddByBundleIdDialog({ open, onOpenChange, onAdd }: Props) {
  const [tab, setTab] = useState<"app" | "webclip">("app");
  const [bundleId, setBundleId] = useState("");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  // Verdict captured by the live preview probe so we can persist it on the
  // IconItem and keep the canvas visually consistent across refresh and
  // export/import without re-running the probe.
  const [resolvedKind, setResolvedKind] = useState<IconKind | null>(null);

  function reset() {
    setBundleId("");
    setName("");
    setUrl("");
    setResolvedKind(null);
    setTab("app");
  }

  function handleAdd() {
    if (tab === "app") {
      if (!bundleId.trim()) return;
      onAdd({
        Type: "Application",
        BundleID: bundleId.trim(),
        DisplayName: name.trim() || undefined,
      });
    } else {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) return;
      const resolvedIcon = getFaviconUrl(trimmedUrl) ?? undefined;
      const displayName = name.trim() || undefined;
      onAdd({
        Type: "WebClip",
        URL: trimmedUrl,
        DisplayName: displayName,
        // Persist the resolved favicon so the canvas keeps showing the site
        // glyph without re-resolving on every render.
        Icon: resolvedIcon,
        // Persist the verdict so refresh / export / re-import render the
        // same icon (real favicon vs. generic fallback) immediately.
        IconKind: resolvedKind ?? undefined,
      });
      // Mirror into the local-recents store so this clip appears in the
      // Web Clips library tab on subsequent sessions, even before it's
      // saved into a configuration.
      recordRecentWebClip({
        url: trimmedUrl,
        displayName,
        icon: resolvedIcon,
        iconKind: resolvedKind ?? undefined,
      });
    }
    reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add icon manually</DialogTitle>
          <DialogDescription>
            Place an app by bundle identifier or pin a Web Clip directly to the layout.
          </DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={(v) => setTab(v as "app" | "webclip")}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="app">Application</TabsTrigger>
            <TabsTrigger value="webclip">Web Clip</TabsTrigger>
          </TabsList>
          <TabsContent value="app" className="space-y-3 pt-3">
            <div className="space-y-1.5">
              <Label htmlFor="bundle-id">Bundle ID</Label>
              <Input
                id="bundle-id"
                placeholder="com.example.app"
                value={bundleId}
                onChange={(e) => setBundleId(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="display-name">Display name (optional)</Label>
              <Input
                id="display-name"
                placeholder="App name shown on device"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </TabsContent>
          <TabsContent value="webclip" className="space-y-3 pt-3">
            <div className="space-y-1.5">
              <Label htmlFor="webclip-url">URL</Label>
              <Input
                id="webclip-url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="webclip-name">Display name (optional)</Label>
              <Input
                id="webclip-name"
                placeholder="Web clip label"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <WebClipPreview url={url} name={name} onIconKindResolved={setResolvedKind} />
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleAdd}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WebClipPreview({
  url,
  name,
  onIconKindResolved,
}: {
  url: string;
  name: string;
  onIconKindResolved?: (kind: IconKind) => void;
}) {
  const trimmed = url.trim();
  const hasHost = useMemo(() => !!getFaviconUrl(trimmed), [trimmed]);
  const label = name.trim() || (trimmed ? hostnameOrRaw(trimmed) : "Web Clip");
  const [status, setStatus] = useState<WebClipFaviconStatus>("idle");
  // Track the verdict locally so the preview can show what will be persisted
  // alongside the clip when the user clicks Add.
  const [persistedKind, setPersistedKind] = useState<IconKind | null>(null);

  // Reset persisted verdict whenever the URL changes (different host = different probe).
  useEffect(() => {
    setPersistedKind(null);
  }, [trimmed]);

  let statusText: string;
  let statusClass = "text-muted-foreground";
  if (!hasHost) {
    statusText = "Enter a URL to preview the favicon";
  } else if (status === "loading") {
    statusText = "Fetching favicon…";
  } else if (status === "error") {
    statusText = "Couldn't load favicon — using generic icon";
    statusClass = "text-destructive";
  } else if (status === "generic") {
    statusText = "No favicon found for this site — using generic icon";
  } else if (status === "loaded") {
    statusText = "Favicon auto-fetched from site";
  } else {
    statusText = "Favicon auto-fetched from site (generic globe shown if none found)";
  }

  const showNoFaviconBadge = hasHost && (status === "generic" || status === "error");
  const badgeText = status === "error" ? "Favicon unavailable" : "No favicon found";

  return (
    <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3">
      <WebClipFavicon
        url={trimmed}
        label={label}
        className="h-12 w-12"
        onStatusChange={setStatus}
        onIconKindResolved={(kind) => {
          setPersistedKind(kind);
          onIconKindResolved?.(kind);
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <p className="text-sm font-medium truncate">{label}</p>
          {showNoFaviconBadge && (
            <Badge variant="secondary" className="shrink-0 text-[10px] uppercase tracking-wide">
              {badgeText}
            </Badge>
          )}
          {persistedKind && (
            // Make the verdict that will be saved with the clip explicit so
            // users understand why the same icon will reappear after refresh
            // / re-import without another network probe.
            <Badge
              variant="outline"
              className={cn(
                "shrink-0 text-[10px] uppercase tracking-wide",
                persistedKind === "favicon"
                  ? "border-success/40 bg-success-muted text-success-muted-foreground"
                  : "border-border bg-muted text-muted-foreground"
              )}
              data-testid={`webclip-preview-iconkind-${persistedKind}`}
            >
              Will save as: {persistedKind === "favicon" ? "Favicon" : "Generic fallback"}
            </Badge>
          )}
        </div>
        <p className={cn("text-xs truncate", statusClass)} aria-live="polite">
          {statusText}
        </p>
      </div>
    </div>
  );
}

function hostnameOrRaw(input: string): string {
  try {
    return new URL(input.includes("://") ? input : `https://${input}`).hostname;
  } catch {
    return input;
  }
}
